// Engine-neutral paging + main-thread-yielding helpers for schema
// introspection (SLICES.md § S7 items 2 + 7).
//
// Why paging at all: before S7 every introspection fetcher issued ONE query
// with a 50,000-row cap and read the whole result set out of a single IPC
// message (`result.resultSets[0].rows`). On a large production database that
// meant (a) a multi-megabyte inline array crossing the bridge, (b) a
// synchronous merge + projection pass long enough to freeze the renderer for
// seconds, and (c) a hard 50k ceiling that silently clipped the tail of the
// catalog (ordered by schema/table, so late-alphabet tables lost EVERYTHING).
//
// The fix is server-side paging: every introspection query gets an
// `ORDER BY <stable key> OFFSET n ROWS FETCH NEXT m ROWS ONLY` tail, pages are
// `INTROSPECTION_PAGE_ROWS` rows, and each page is aggregated + merged as it
// arrives with an event-loop yield in between. Two invariants come out of it:
//
//   1. **Page size ≤ the query cache's fast-path threshold**
//      (`FAST_PATH_MAX_ROWS`, 50,000 — see the "Cap constraint" note in
//      `introspectionTruncation.ts`). A page never gets windowed, so its rows
//      always arrive inline.
//   2. **Total row budgets are a memory bound, not an IPC bound.** A budget
//      (`*_ROW_BUDGET` in the per-artefact fetchers) may be far above the
//      fast-path threshold because it is spent across many pages.
//
// Engine-neutral on purpose: S7 item 8 rolls the same treatment out to
// `pgSchemaCache` / `mysqlSchemaCache` / `oracleSchemaCache` /
// `sqliteSchemaCache`, which reuse this module rather than copying the loop.

/**
 * Rows per introspection page. Pinned: raising it above the query cache's
 * `FAST_PATH_MAX_ROWS` (50,000) would re-introduce the windowed-and-stripped
 * failure mode invariant 1 exists to prevent, and lowering it multiplies
 * round-trips for no benefit.
 */
export const INTROSPECTION_PAGE_ROWS = 5_000;

/**
 * The per-page `maxRows` guard handed to `executeQueryV2`. Same value as
 * `INTROSPECTION_PAGE_ROWS` (pinned equal by `introspectionPaging.test.ts`),
 * carrying the `_MAX_ROWS` suffix the cap pin in `introspectionTruncation.test.ts`
 * scans for. Truncation is strict (`rows.length > maxRows`) in every adapter,
 * so a full page of exactly `INTROSPECTION_PAGE_ROWS` rows never trips
 * `stats.truncated`.
 */
export const INTROSPECTION_PAGE_MAX_ROWS = 5_000;

/**
 * Items merged per main-thread chunk when a merge/projection pass walks an
 * already-fetched array (as opposed to a freshly-fetched page). Sized so one
 * chunk stays far below the 50 ms long-task threshold the perf gate
 * (`snapshotLoadLongTasks.perf.spec.ts`) asserts on.
 */
export const INTROSPECTION_MERGE_CHUNK_ITEMS = 1_000;

export type IntrospectionRow = Record<string, unknown>;

/** One page's worth of raw rows, as the caller's `executeQueryV2` wrapper saw it. */
export type IntrospectionPageResult = {
  /** `false` when the query failed — paging stops and `failed` is reported. */
  readonly ok: boolean;
  readonly rows: readonly IntrospectionRow[];
  /** `QueryExecutionResult.stats.truncated` — defensive; a page should never trip it. */
  readonly truncated?: boolean;
  /** The engine's error message when `ok` is false, for the failure record. */
  readonly error?: string;
};

export type PagedIntrospectionOutcome = {
  /** A page query failed; rows delivered before it are still merged. */
  readonly failed: boolean;
  /** The failed page's error message, when the bridge supplied one. */
  readonly error?: string;
  /** The total row budget was exhausted while rows were (probably) still pending. */
  readonly capHit: boolean;
  /** `shouldAbort` stopped the fetch — nothing about the catalog may be inferred. */
  readonly aborted: boolean;
  readonly rowsRead: number;
  readonly pages: number;
};

/**
 * Did this fetch read the WHOLE artefact? Only then may a merge treat what it
 * did not see as deleted: a failed, aborted or budget-capped fetch says
 * nothing about the rows it never reached, so carried-over metadata must
 * stand (release review 1.5.0, F5).
 */
export function isCompleteOutcome(outcome: PagedIntrospectionOutcome | undefined): boolean {
  return outcome !== undefined && !outcome.failed && !outcome.aborted && !outcome.capHit;
}

export type PagedIntrospectionOptions<TItem> = {
  /** Issues ONE page query. `offset`/`limit` go straight into OFFSET/FETCH. */
  readonly fetchPage: (page: {
    readonly offset: number;
    readonly limit: number;
  }) => Promise<IntrospectionPageResult>;
  /** Row-shape → domain-shape conversion for one page (the existing `aggregate*Rows`). */
  readonly aggregate: (rows: readonly IntrospectionRow[]) => TItem[];
  /** Receives each page's aggregated items. May be async (merge + republish). */
  readonly sink: (items: readonly TItem[]) => void | Promise<void>;
  /** Hard TOTAL row budget across all pages (a memory bound — see the header). */
  readonly totalBudget: number;
  readonly pageRows?: number;
  /**
   * Group identity for aggregators that fold MANY rows into one item (an FK's
   * columns, an index's key columns, a routine's parameters). Rows sharing the
   * last row's group key are held back and prepended to the next page, so a
   * group straddling a page boundary is never aggregated twice as two partial
   * items. Omit for row-per-item artefacts (columns, CHECK clauses, tables).
   */
  readonly groupKeyOf?: (row: IntrospectionRow) => string;
  /** Overridable for tests. Defaults to `yieldToEventLoop`. */
  readonly yieldBetweenPages?: () => Promise<void>;
  /**
   * Checked before every page: `true` stops the fetch where it stands. The
   * mssql cache passes its generation check here so a refresh that supersedes
   * an in-flight phase 2 also stops that phase's remaining page queries
   * instead of paging a catalog nobody will merge.
   */
  readonly shouldAbort?: () => boolean;
};

/**
 * Hand the event loop a turn. Uses `scheduler.yield()` where the runtime has
 * it (Electron 33+/Chromium 129+ — a continuation-priority yield that comes
 * back before unrelated macrotasks) and falls back to `setTimeout(0)`.
 */
export async function yieldToEventLoop(): Promise<void> {
  const scheduler = (
    globalThis as unknown as { scheduler?: { yield?: () => Promise<void> } }
  ).scheduler;
  if (scheduler && typeof scheduler.yield === 'function') {
    try {
      await scheduler.yield();
      return;
    } catch {
      // Fall through to the timer path — `scheduler.yield()` rejects when the
      // document is hidden in some Chromium builds.
    }
  }
  await new Promise<void>((resolve) => {
    setTimeout(resolve, 0);
  });
}

/**
 * Fetch an introspection artefact page by page, aggregating and sinking each
 * page as it lands with an event-loop yield in between. Resolves once the
 * server runs out of rows, a page fails, or the total budget is spent.
 *
 * A fetch that completes successfully with NO rows still calls `sink([])`
 * once: the merge layer replaces carried-over metadata on the first page it
 * receives, so an empty catalog that never reached the sink would leave the
 * previous refresh's FKs / routines / sequences standing after the user had
 * dropped the last one (release review 1.5.0, F5). Failed, aborted and
 * budget-capped fetches never synthesize that empty page — they say nothing
 * about the rows they did not read.
 */
export async function fetchIntrospectionPaged<TItem>(
  options: PagedIntrospectionOptions<TItem>,
): Promise<PagedIntrospectionOutcome> {
  const pageRows = options.pageRows ?? INTROSPECTION_PAGE_ROWS;
  const yieldFn = options.yieldBetweenPages ?? yieldToEventLoop;
  let offset = 0;
  let rowsRead = 0;
  let pages = 0;
  let capHit = false;
  let sunk = false;
  let carry: readonly IntrospectionRow[] = [];

  for (;;) {
    if (options.shouldAbort?.()) return { failed: false, capHit, aborted: true, rowsRead, pages };
    const remaining = options.totalBudget - rowsRead;
    if (remaining <= 0) {
      capHit = true;
      break;
    }
    const limit = Math.min(pageRows, remaining);
    const page = await options.fetchPage({ offset, limit });
    // Re-check AFTER the await: a load superseded while this page was in
    // flight must not report the page's failure (or its rows) as its own —
    // a cancelled old query would otherwise mark the NEWER, successful load
    // as failed (release review 1.5.0, round 3 N2).
    if (options.shouldAbort?.()) return { failed: false, capHit, aborted: true, rowsRead, pages };
    if (!page.ok) {
      if (carry.length > 0) await options.sink(options.aggregate(carry));
      return { failed: true, error: page.error, capHit, aborted: false, rowsRead, pages };
    }
    pages += 1;
    const rows = page.rows;
    rowsRead += rows.length;
    offset += rows.length;
    // A short page means the server has no more rows for this artefact.
    const lastPage = rows.length < limit;
    const budgetSpent = rowsRead >= options.totalBudget;
    if (page.truncated) capHit = true;

    const combined = carry.length > 0 ? [...carry, ...rows] : rows;
    let batch: readonly IntrospectionRow[] = combined;
    carry = [];
    if (!lastPage && !budgetSpent && options.groupKeyOf && combined.length > 0) {
      const tailKey = options.groupKeyOf(combined[combined.length - 1]);
      let cut = combined.length;
      while (cut > 0 && options.groupKeyOf(combined[cut - 1]) === tailKey) cut -= 1;
      batch = combined.slice(0, cut);
      carry = combined.slice(cut);
    }
    if (batch.length > 0) {
      sunk = true;
      await options.sink(options.aggregate(batch));
    }

    if (lastPage) break;
    if (budgetSpent) {
      capHit = true;
      break;
    }
    await yieldFn();
  }

  if (carry.length > 0) {
    sunk = true;
    await options.sink(options.aggregate(carry));
  }
  // Successful, complete, empty: tell the sink so it can clear what it carried.
  if (!sunk && !capHit) await options.sink([]);
  return { failed: false, capHit, aborted: false, rowsRead, pages };
}

/**
 * Walk `items` in `INTROSPECTION_MERGE_CHUNK_ITEMS`-sized chunks, yielding to
 * the event loop between chunks so no single main-thread task exceeds the
 * 50 ms long-task threshold. Used by the snapshot projection pass, which is
 * the one merge step that is not already page-shaped.
 */
export async function forEachChunkYielding<TItem>(
  items: readonly TItem[],
  apply: (chunk: readonly TItem[]) => void,
  options?: {
    readonly chunkSize?: number;
    readonly yieldBetweenChunks?: () => Promise<void>;
  },
): Promise<void> {
  const chunkSize = Math.max(1, options?.chunkSize ?? INTROSPECTION_MERGE_CHUNK_ITEMS);
  const yieldFn = options?.yieldBetweenChunks ?? yieldToEventLoop;
  for (let start = 0; start < items.length; start += chunkSize) {
    apply(items.slice(start, start + chunkSize));
    if (start + chunkSize < items.length) await yieldFn();
  }
}

/**
 * Build the `OFFSET … FETCH NEXT …` tail for a T-SQL introspection page.
 * Values are coerced to non-negative integers at the call site boundary so a
 * caller can never splice anything but digits into the SQL text.
 */
export function offsetFetchClause(page: { offset: number; limit: number }): string {
  const offset = Math.max(0, Math.trunc(page.offset));
  const limit = Math.max(1, Math.trunc(page.limit));
  return `OFFSET ${offset} ROWS FETCH NEXT ${limit} ROWS ONLY`;
}

/**
 * The `LIMIT … OFFSET …` spelling of the same window, for PostgreSQL, MySQL
 * and SQLite. Same integer coercion as {@link offsetFetchClause}: a caller can
 * never splice anything but digits into the SQL text.
 */
export function limitOffsetClause(page: { offset: number; limit: number }): string {
  const offset = Math.max(0, Math.trunc(page.offset));
  const limit = Math.max(1, Math.trunc(page.limit));
  return `LIMIT ${limit} OFFSET ${offset}`;
}
